import identityRequest from '../sdk/IdentityRequest';
import step from '../config/Step';

export class ManagerService {

    DEFAULT_COMPONENT = step.data['home_without_code'].value;
    ERROR_COMPONENT = 'error';
    SUCCESS_RESPONSE = true;
    OK_SUCCESS_RESPONSE = 'OK';
    FINAL_STEP_RESPONSE = 'FINAL';
    ERROR_RESPONSE = false;
    DENIED_ACCESS_MESSAGE = 'login';
    UNKNOWN_ERROR_MESSAGE = 'default';
    ANALYSIS_ERROR_DATA = 'data';

    STEPS_WITH_REDIRECT = [
        'select_authentication_type',
        'select_authentication_type_clave_unica_priority',
        'advanced_electronic_signature_validation',
        'rekognition_face_liveness_init',
    ]

    ORIGIN_WITHOUT_REDIRECT = [
        'ECERT',
        'LIVENESS'
    ]

    /**
     * Inicia el proceso de identificación Autofact en base al código que viene como parámetro en la url, o en su defecto,
     * por el código ingresado manualmente a través del formulario
     * @param code código del proceso
     */
    initProcess = async (code, password, resumeToken) => {
        let tokenData, result;
        if (code !== undefined) {
            try {
                tokenData = await identityRequest.init(code, password, resumeToken);
                const token = tokenData.data.token;
                const expiration = tokenData.data.expiration;
                const expirationSecondsToken = tokenData.data.expirationSecondsToken;
                const qs = { code, "include": true };
                const processData = await identityRequest.getResume(token, qs);
                const externName = processData.data.identity_person.extern_name;
                const identityCode = processData.data.identity_person.identity_code;
                const extra = processData.data.extra || {};
                const canInitDesktop = extra ? extra.can_sign_desktop === true || extra.can_sign_desktop === 'true' : false;
                const contractURL = extra.contract_url && (typeof (extra.contract_url) === 'object' ? extra.contract_url : [extra.contract_url]);
                const documents = extra.documents || contractURL || [];
                const dynamicDocuments = extra.dynamicDocuments || [];
                const passwordType = tokenData.data.type;
                const source = processData.data.source;
                const identityType = processData.data.type;
                const companyLogo = extra.companyLogo ?? undefined;
                const plate = extra.plate ?? '';
                const contractResumeInfo = extra.contractResumeInfo || {};
                const company_id = processData.data?.company_id

                result = {
                    token,
                    canInitDesktop,
                    companyLogo,
                    passwordType,
                    expiration,
                    expirationSecondsToken,
                    name: externName,
                    rut: identityCode,
                    status: this.SUCCESS_RESPONSE,
                    component: tokenData.data.component,
                    errorLogin: tokenData.data.errorLogin,
                    source,
                    identityType,
                    documents: documents.concat(dynamicDocuments.map(document => document.url)),
                    plate,
                    contractResumeInfo,
                    company_id
                };

                if (tokenData.data.isMultiple) {
                    result.multiple = true;
                    const plates = await identityRequest.getPlates(token, code);
                    result.plates = plates.data;
                }

            } catch (error) {
                console.error(error);
                result = {
                    status: this.ERROR_RESPONSE,
                    message: this.DENIED_ACCESS_MESSAGE,
                    data: (error && error.error && error.error.data) || '',
                };
            }
        } else {
            result = {
                'status': this.SUCCESS_RESPONSE,
                'component': this.DEFAULT_COMPONENT
            };
        }
        return result;
    };

    getAllDataToResult = async (code) => {
        let result = {};
        try {
            const parcialResult = await identityRequest.getResume(code);
            const dataProcess = parcialResult.data.identity_data;
            const dataToShow = [
                'IDENTITY_CARD_FACE_CROPPED',
                'FACE_PHOTO_CROPPED',
                'FACIAL_PERCENT',
                'IDENTITY_CARD_SIGNATURE_CROPPED',
                'HANDWRITTEN_SIGNATURE',
                'RECORDED_VIDEO'
            ];
            dataProcess.forEach(function (value) {
                if (dataToShow.includes(value.type)) {
                    result[value.type] = value.data;
                }
            });
            result['EXTERN_DATA'] = parcialResult.data.extern_data;
        } catch (error) {
            result = {
                'status': this.ERROR_RESPONSE,
                'message': this.ANALYSIS_ERROR_DATA
            };
        }
        return result;
    };

    /**
     * Administra el avance del proceso en base a los datos almacenados en la BD
     * @param code código del proceso
     * @param token token del proceso
     * @param action action a realizar (avanzar, retroceder, solocitar información del proceso) por defecto obtiene la información del paso
     * @param body datos para enviar del proceso
     */
    stepManager = async (code, token, action = '', body = {}, extra = {}) => {
        let result;
        try {
            if (action === '') {
                const stepResult = await identityRequest.getStep(code, token, extra);
                const component = stepResult.data.name;
                const { error, info, data } = stepResult.data;
                result = {
                    'status': this.SUCCESS_RESPONSE,
                    'component': component,
                    info,
                    data,
                    error,
                };
                const origin = extra && extra.origin || '';
                console.log(this.STEPS_WITH_REDIRECT.includes(component) && info && info.redirectUrl && !this.ORIGIN_WITHOUT_REDIRECT.includes(origin));
                if (this.STEPS_WITH_REDIRECT.includes(component) && info && info.redirectUrl && !this.ORIGIN_WITHOUT_REDIRECT.includes(origin)) {
                    result.redirectUrl = true;
                    window.location.assign(info.redirectUrl);
                }
            } else if (action === 'next') {
                const stepResult = await identityRequest.postStep(code, token, body);
                if (stepResult.data.status === this.SUCCESS_RESPONSE) {
                    if (stepResult.data.message === 'FINAL') {
                        result = {
                            'status': this.SUCCESS_RESPONSE,
                            'component': this.DEFAULT_COMPONENT,
                            'info': {},
                            'data': {}
                        };
                    } else {
                        return await this.stepManager(code, token);
                    }
                } else {
                    result = {
                        'status': this.ERROR_RESPONSE,
                        'message': stepResult.data.message
                    };
                }
            } else if (action === 'prev') {
                //TODO back state implementation in other versions
            } else {
                throw this.DENIED_ACCESS_MESSAGE;
            }
        } catch (error) {
            result = {
                'status': this.ERROR_RESPONSE,
                'message': this.ANALYSIS_ERROR_DATA
            };
        }
        return result;
    };

    getProcessData = async (code) => {
        let result;
        if (code) {
            const request = await identityRequest.getProcessData(code);
            if (request.status === this.OK_SUCCESS_RESPONSE) {
                result = request.data;
            }
        }
        return result;
    }

    /**
     * Almacena errores que solo tenemos definidos para el front
     * @param code código del proceso
     * @param errorCode código del error
     * @param title titulo del error
     * @param description descripcion del error
     */
    saveError = async (code, errorCode, title, description) => {
        let result;
        try {
            const request = await identityRequest.saveError(code, errorCode, title, description);
            if (request.status === this.OK_SUCCESS_RESPONSE) {
                result = request.data;
            }
        } catch (error) {
            console.error('Error during the process to save errors that we only have defined for the front:', error);
        }
        return result;
    };
}

export default new ManagerService();

